import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Search, Package, Check, Loader2, Plus } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BrowseCatalogModal = ({ shopId, onClose, onAdded }) => {
  const { api } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/shops/${shopId}/browse-catalog`);
        setProducts(res.data.products);
        setCategories(res.data.categories);
      } catch { toast.error('Failed to load catalog'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [api, shopId]);

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name_en?.toLowerCase().includes(q) || p.name_es?.toLowerCase().includes(q) || p.barcode?.includes(q);
    const matchCat = !filterCat || p.category_en === filterCat;
    return matchSearch && matchCat;
  });

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      const res = await api.post(`/shops/${shopId}/add-from-catalog`, { catalog_ids: [...selected] });
      toast.success(res.data.message);
      onAdded?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    finally { setAdding(false); }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-3xl bg-white flex flex-col max-h-[85vh]"
          onClick={e => e.stopPropagation()} data-testid="browse-catalog-modal">

          {/* Header */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-lg font-bold text-[#1e3a5f]">Browse Global Catalog</h3>
              <p className="text-sm text-slate-500">{products.length} products available</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-sm" data-testid="browse-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search + Filter */}
          <div className="p-4 border-b border-slate-100 flex gap-3 flex-shrink-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search catalog..."
                className="w-full pl-10 pr-4 py-2 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                data-testid="browse-search" />
            </div>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className="px-3 py-2 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
              data-testid="browse-category-filter">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Product List */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1e3a5f]" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center text-slate-500">No products found</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filtered.map(p => (
                  <button key={p.id} onClick={() => toggleSelect(p.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      selected.has(p.id) ? 'bg-green-50' : 'hover:bg-slate-50'
                    }`} data-testid={`browse-item-${p.id}`}>
                    <div className={`w-6 h-6 border-2 rounded-sm flex items-center justify-center flex-shrink-0 ${
                      selected.has(p.id) ? 'bg-[#22c55e] border-[#22c55e]' : 'border-slate-300'
                    }`}>
                      {selected.has(p.id) && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1e3a5f] text-sm">{p.name_en}</p>
                      <p className="text-xs text-slate-400">{p.name_es} &middot; {p.category_en}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-[#22c55e]">{'\u20AC'}{p.price?.toFixed(2)}</p>
                      {p.barcode && <p className="text-xs text-slate-400 font-mono">{p.barcode}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
            <span className="text-sm text-slate-500">{selected.size} selected</span>
            <div className="flex gap-3">
              <button onClick={onClose} className="vf-btn-secondary">Cancel</button>
              <button onClick={handleAdd} disabled={adding || selected.size === 0}
                className="vf-btn-primary flex items-center gap-2 disabled:opacity-50"
                data-testid="browse-add-btn">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add {selected.size} to Shop
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default BrowseCatalogModal;
